import React from "react";
import type { FC, SVGProps } from "react";

import { Icon } from "@/atoms/icon/Icon";
import ChevronRight from "@/atoms/icon/icons/chevron_right.svg";
import Calendar from "@/atoms/icon/icons/ic_calendar.svg";
import Offer from "@/atoms/icon/icons/ic_offers_ol.svg";
import Info from "@/atoms/icon/icons/info.svg";
import LinkIcon from "@/atoms/icon/icons/link.svg";
import Phone from "@/atoms/icon/icons/phone.svg";
import { cn } from "@/lib/utils";

import { useAdonisChat } from "../../AdonisChatProvider/AdonisChatProvider";
import { getButtonType } from "../../utils/buttonIdentifier";

export type AdonisCtaIcon =
  | "calendar"
  | "cart"
  | "gift"
  | "phone"
  | "phone_tilt"
  | "link"
  | "location"
  | "info"
  | "check"
  | "star"
  | "alert"
  | "offer"
  | "settings";

export interface AdonisCtaButtonProps {
  label: string;
  icon?: AdonisCtaIcon;
  href?: string;
  onClick?: () => void;
}

export const AdonisCtaButton = ({
  label,
  icon = "info",
  href,
  onClick,
}: AdonisCtaButtonProps) => {
  const { platformLocation } = useAdonisChat();

  const className = cn(
    "inline-flex w-full items-center gap-2 rounded-md border border-[#AF936E] bg-white px-3 py-2 text-sm font-medium text-primary shadow-sm",
    "hover:bg-[#FFF1E0] active:bg-[#F8E8D2] transition-colors"
  );

  const { svg: StartIcon, rotateClass } = getIconWithRotation(icon);

  // Determine button type for tracking
  const buttonType = getButtonType(href, platformLocation);

  const innerContent = (
    <>
      <Icon
        svg={StartIcon}
        color="currentColor"
        size="xsmall"
        className={cn("shrink-0", rotateClass)}
      />
      <span className="flex-1 truncate text-left">{label}</span>
      <Icon
        svg={ChevronRight}
        color="currentColor"
        size="x3small"
        className="ml-auto shrink-0"
      />
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={className}
        onClick={onClick}
        data-button-type={buttonType}
        data-contact-method={
          buttonType === "contact-phone"
            ? "phone"
            : buttonType === "contact-email"
            ? "email"
            : undefined
        }
      >
        {innerContent}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      data-button-type={buttonType}
    >
      {innerContent}
    </button>
  );
};

function getIconWithRotation(name: AdonisCtaIcon): {
  svg: FC<SVGProps<SVGSVGElement>>;
  rotateClass?: string;
} {
  switch (name) {
    case "calendar":
      return { svg: Calendar };
    case "phone":
      return { svg: Phone, rotateClass: "rotate-90" };
    case "offer":
      return { svg: Offer };
    case "link":
      return { svg: LinkIcon };
    case "info":
      return { svg: Info };
    default:
      return { svg: Info };
  }
}

export default AdonisCtaButton;
